import React, { useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  SvgIcon,
  Tooltip
} from '@material-ui/core';
import { DialogWithFocusProvider } from 'components/layout';
import ClearIcon from '@material-ui/icons/Clear';
import {
  setPostAcqViewContainersDialogIsOpen,
  setPostAcqSelectedContainerId,
  fetchPostAcqUWContainers,
  setViewContainersDialogIsOpen,
  setSelectedContainerId,
  fetchPropertyUWContainers
} from 'store/actions';
import { Scrolling, Column, HeaderFilter } from "devextreme-react/data-grid";
import DataGrid from 'components/devExtreme/DataGrid';
import { Stack, LoadingOverlayWrapper } from "components/layout";
import './ViewContainersModal.css';
import { Constants } from 'utilities/constants';
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';

const ViewContainersModal = ({
  classes,
  txnOppId,
  containers,
  isFetchingContainers,
  fetchUWContainers,
  viewContainersDialogIsOpen,
  setViewContainersDialogIsOpen,
  selectedContainerId,
  setSelectedContainerId,
  valuationIsDirty
}) => {
  const { openSaveUWDialog } = useSaveUWDialog();

  useEffect(() => {
    if (!viewContainersDialogIsOpen) return;
    fetchUWContainers(txnOppId);
  }, [viewContainersDialogIsOpen])

  const handleClose = () => {
    setViewContainersDialogIsOpen(false)
  }

  const handleContainerClick = (e) => {
    const container = e.row.data;
    const containerId = container.isLatest ? null : container.id;
    if (valuationIsDirty) {
      openSaveUWDialog({ 
        callback: () => {
          setSelectedContainerId(containerId);
          setViewContainersDialogIsOpen(false);
        }
      });
    } else {
      setSelectedContainerId(containerId);
      setViewContainersDialogIsOpen(false);
    }
  }

  const onCellPrepared = cell => {
    if (cell.rowType === 'data' && cell.column.dataType === 'boolean') {
      cell.cellElement.style.fontSize = '20px';
      cell.cellElement.style.color = 'rgba(0, 0, 0, 0.65)';
    }
  }

  const applyColumnRender = (e) => {
    const { id, isLatest } = e.row.data;
    const isSelected = id === selectedContainerId || (isLatest && selectedContainerId === null);
    return (
			<Stack horizontal>
				<Tooltip title={isLatest ? "Return to latest" : "View in read-only mode"} placement="bottom">
          <span>
            <IconButton
              onClick={() => {
                handleContainerClick(e);
              }}
              size='small'
              color='inherit'
              className={classes.btnApply}
              disabled={isSelected}
            >
              <SvgIcon
                style={{ height: 20, width: 20 }}
              >
                <path d="M19.78,2.2L24,6.42L8.44,22L0,13.55L4.22,9.33L8.44,13.55L19.78,2.2M19.78,5L8.44,16.36L4.22,12.19L2.81,13.55L8.44,19.17L21.19,6.42L19.78,5Z" />
              </SvgIcon>
            </IconButton>
          </span>
				</Tooltip>
			</Stack>
    );
  };

  const latestContainerId = containers?.find(container => container.isLatest)?.id;
  const hasSomeFailedOn = containers?.some(container => container.failedOn != null);

  return (
    <DialogWithFocusProvider onClose={handleClose} open={viewContainersDialogIsOpen} className={classes.dialog} maxWidth={'xl'}>
      <DialogTitle>
        {`View Reloads`}
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent>
        <LoadingOverlayWrapper
          active={isFetchingContainers}
          message={'Loading containers...'}
        >
          <DataGrid
            id='viewContainersGrid'
            keyExpr={'id'}
            persistState
            dataSource={containers}
            height={260}
            paging={{ enabled: false }}
            hoverStateEnabled
            selectedRowKeys={[selectedContainerId ?? latestContainerId]}
            onCellPrepared={onCellPrepared}
            loadPanel={{ enabled: false }}
          >
            <Column width={48} cellRender={applyColumnRender} />
            <Column dataField={"id"} caption={"Container ID"} sortOrder={"desc"} width={118}/>
            <Column dataField={"completedOn"} dataType={"date"} format={"MM/dd/yyyy, h:mm:ss a"} width={170} />
            {hasSomeFailedOn &&
              <Column dataField={"failedOn"} dataType={"date"} format={"MM/dd/yyyy, h:mm:ss a"} width={170} />
            }
            <Column dataField={"initialOpportunityStatus"} dataType={"string"} width={148} />
            <Column dataField={"initialUnderwriteStatus"} dataType={"string"} width={148} />
            <Column dataField={"lastOpportunityStatus"} dataType={"string"} width={148} />
            <Column dataField={"lastUnderwriteStatus"} dataType={"string"} width={148} />
            <Column dataField={"createdBy"} dataType={"string"} width={150} />
            <Column dataField={"lastSavedUnderwriteUser"} dataType={"string"} width={150} />
            <Column dataField={"importDetails"} dataType={"string"} width={130} />
            <Column dataField={"reloadDetails"} dataType={"string"} width={130} />
            <Column dataField={"compsCount"} dataType={"number"} width={130}/>
            <Column dataField={"isLatest"} dataType={"boolean"} width={130} showEditorAlways={false} trueText={'✔'} falseText={'✖'} />
            <Column width='auto'/>
            <Scrolling
              rowRenderingMode='virtual'
            />
            <HeaderFilter
              visible={true}
              allowSearch={true}
              hideSelectAllOnSearch={false}
            />
          </DataGrid>
        </LoadingOverlayWrapper>
      </DialogContent>
      <DialogActions>
        <></>
      </DialogActions>
    </DialogWithFocusProvider>
  )
}

const styles = theme => ({
  dialog: {
    minWidth: '200px',
    minHeight: '150px'
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  btnApply: {
    padding: 2,
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: `5px`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
  },
});

const mapState = (state, ownProps) => {
  const moduleState = ownProps.module === Constants.Module.POSTACQ ? state.postAcqUWState : state.propertyUWState;
  return {
    viewContainersDialogIsOpen: moduleState.viewContainersDialogIsOpen,
    containers: moduleState.containers,
    isFetchingContainers: moduleState.isFetchingContainers,
    selectedContainerId: moduleState.selectedContainerId,
    valuationIsDirty: moduleState.valuationIsDirty,
  }
};

const mapDispatch = (dispatch, ownProps) => {
  const actions = ownProps.module === Constants.Module.POSTACQ
    ? {
      setViewContainersDialogIsOpen: setPostAcqViewContainersDialogIsOpen,
      setSelectedContainerId: setPostAcqSelectedContainerId,
      fetchUWContainers: fetchPostAcqUWContainers
    }
    : {
      setViewContainersDialogIsOpen,
      setSelectedContainerId,
      fetchUWContainers: fetchPropertyUWContainers
    };
  return {
    setViewContainersDialogIsOpen: isOpen => dispatch(actions.setViewContainersDialogIsOpen(isOpen)),
    setSelectedContainerId: (selectedContainerId, selectedValuationId, transientValuation, transactionOpportunityId, shouldEndLoading, callback) => dispatch(actions.setSelectedContainerId(selectedContainerId, selectedValuationId, transientValuation, transactionOpportunityId, shouldEndLoading, callback)),
    fetchUWContainers: txnOppId => dispatch(actions.fetchUWContainers(txnOppId))
  }
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(ViewContainersModal);